import type { A2uiAction } from '../components/a2ui/A2UISurface';
import type { MessageA2uiSurface } from '../types/chat';

interface SurfaceComponentInfo {
  type: string;
  label?: string;
  optionLabels?: Map<string, string>;
}

/** Value-bound components whose submit renders as `Label: value` in message content. */
const LABELED_VALUE_TYPES = new Set([
  'CheckBox',
  'Slider',
  'DateTimeInput',
  'TextField',
  'Select',
  'MultiSelect',
]);

/** Component id → type/options index built from the surface's accumulated spec messages. */
function collectComponents(surface: MessageA2uiSurface): Map<string, SurfaceComponentInfo> {
  const map = new Map<string, SurfaceComponentInfo>();
  for (const message of surface.messages) {
    const components = (message.updateComponents as { components?: unknown[] } | undefined)
      ?.components;
    if (!Array.isArray(components)) continue;
    for (const entry of components) {
      const component = entry as {
        id?: string;
        component?: string;
        label?: unknown;
        options?: unknown;
      } | null;
      if (!component?.id || !component.component) continue;
      let optionLabels: Map<string, string> | undefined;
      if (Array.isArray(component.options)) {
        optionLabels = new Map(
          component.options
            .filter(
              (option): option is { value: string; label?: string } =>
                !!option && typeof (option as { value?: unknown }).value === 'string'
            )
            .map((option) => [
              option.value,
              typeof option.label === 'string' ? option.label : option.value,
            ])
        );
      }
      map.set(component.id, {
        type: component.component,
        ...(typeof component.label === 'string' && { label: component.label }),
        ...(optionLabels && { optionLabels }),
      });
    }
  }
  return map;
}

export interface A2uiSubmitPayload {
  /** Human-readable user message, e.g. `Selected: A, B — Additional instructions: …`. */
  content: string;
  /** Message `metadata.a2ui` per wire contract §4. */
  metadata: {
    surfaceId: string;
    action: string;
    selections: Record<string, unknown>;
    instructions: string | null;
  };
}

/** Splits a resolved action context into selections/instructions and builds the send payload. */
export function buildA2uiSubmit(
  surface: MessageA2uiSurface,
  action: A2uiAction
): A2uiSubmitPayload {
  const components = collectComponents(surface);
  const selections: Record<string, unknown> = {};
  const labels: string[] = [];
  let instructions = '';

  for (const [key, raw] of Object.entries(action.context ?? {})) {
    if (raw == null) continue;
    const info = components.get(key);

    // Only an explicit `instructions` field maps to metadata.instructions.
    if (key === 'instructions') {
      if (typeof raw === 'string' && raw.trim()) instructions = raw.trim();
      continue;
    }

    selections[key] = raw;

    if (info && LABELED_VALUE_TYPES.has(info.type)) {
      if (info.type === 'MultiSelect' && Array.isArray(raw)) {
        const parts = raw.map(
          (value) => info.optionLabels?.get(String(value)) ?? String(value),
        );
        labels.push(`${info.label ?? key}: ${parts.join(', ')}`);
      } else {
        const text = String(raw);
        labels.push(
          `${info.label ?? key}: ${info.optionLabels?.get(text) ?? text}`,
        );
      }
      continue;
    }

    for (const value of Array.isArray(raw) ? raw : [raw]) {
      const text = String(value);
      labels.push(info?.optionLabels?.get(text) ?? text);
    }
  }

  const clauses = [
    labels.length > 0 && `Selected: ${labels.join(', ')}`,
    instructions && `Additional instructions: ${instructions}`,
  ].filter(Boolean) as string[];

  return {
    content: clauses.join(' — ') || 'Submitted',
    metadata: {
      surfaceId: surface.surfaceId,
      action: action.name || 'submit',
      selections,
      instructions: instructions || null,
    },
  };
}
